<template>
  <simulador-card
    :title="$t('tools.variableAccountsTitle')"
    :info="$t('tools.variableAccountsInfo')"
  >
    <div class="variable-accounts-container">
      <simulador-field
        id="royalties-financeiro"
        :label="$t('tools.royaltiesFinanceiro')"
        type="number"
        :value="royaltiesFinanceiro"
        :min="0"
        :max="100"
        unit="%"
        @input="updateRoyaltiesFinanceiro"
        @change="calcularTotalContasVariaveis"
      />

      <simulador-field
        id="imposto"
        :label="$t('tools.imposto')"
        type="number"
        :value="imposto"
        :min="0"
        :max="100"
        unit="%"
        @input="updateImposto"
        @change="calcularTotalContasVariaveis"
      />

      <simulador-field
        id="repasse"
        :label="$t('tools.repasse')"
        type="number"
        :value="repasse"
        :min="0"
        :max="100"
        :step="0.1"
        unit="%"
        @input="updateRepasse"
        @change="calcularTotalContasVariaveis"
      />

      <simulador-field
        id="lucro"
        :label="$t('tools.lucro')"
        type="number"
        :value="lucro"
        :min="0"
        :max="100"
        :step="0.1"
        unit="%"
        @input="updateLucro"
        @change="calcularTotalContasVariaveis"
      />

      <simulador-field
        id="despesas-representadas-variavel"
        :label="$t('tools.despesasVariaveisLabel')"
        :value="despesasRepresentadasVariavel"
        unit="%"
        :formula="$t('tools.despesasVariaveisFormula')"
        :is-result="true"
      />

      <simulador-field
        id="total-contas-variaveis"
        :label="$t('tools.totalContasVariaveisLabel')"
        :value="totalContasVariaveis"
        unit="%"
        :formula="$t('tools.totalContasVariaveisFormula')"
        :is-result="true"
      />
    </div>
  </simulador-card>
</template>

<script>
import SimuladorCard from "./SimuladorCard.vue";
import SimuladorField from "./SimuladorField.vue";

export default {
  name: "ContasVariaveis",
  components: {
    SimuladorCard,
    SimuladorField
  },
  props: {
    value: {
      type: Object,
      default: () => ({
        royaltiesFinanceiro: 10,
        imposto: 6,
        repasse: 0,
        lucro: 20,
        despesasRepresentadasVariavel: "12,49%",
        totalContasVariaveis: "48,49%"
      })
    },
    percentualDespesas: {
      type: String,
      default: "12,49%"
    }
  },
  computed: {
    royaltiesFinanceiro: {
      get() {
        return this.value.royaltiesFinanceiro;
      },
      set(val) {
        this.$emit("input", { ...this.value, royaltiesFinanceiro: val });
      }
    },
    imposto: {
      get() {
        return this.value.imposto;
      },
      set(val) {
        this.$emit("input", { ...this.value, imposto: val });
      }
    },
    repasse: {
      get() {
        return this.value.repasse;
      },
      set(val) {
        this.$emit("input", { ...this.value, repasse: val });
      }
    },
    lucro: {
      get() {
        return this.value.lucro;
      },
      set(val) {
        this.$emit("input", { ...this.value, lucro: val });
      }
    },
    despesasRepresentadasVariavel: {
      get() {
        return this.value.despesasRepresentadasVariavel;
      },
      set(val) {
        this.$emit("input", {
          ...this.value,
          despesasRepresentadasVariavel: val
        });
      }
    },
    totalContasVariaveis: {
      get() {
        return this.value.totalContasVariaveis;
      },
      set(val) {
        this.$emit("input", { ...this.value, totalContasVariaveis: val });
      }
    }
  },
  methods: {
    updateRoyaltiesFinanceiro(val) {
      this.royaltiesFinanceiro = val;
    },
    updateImposto(val) {
      this.imposto = val;
    },
    updateRepasse(val) {
      this.repasse = val;
    },
    updateLucro(val) {
      this.lucro = val;
    },
    calcularTotalContasVariaveis() {
      // Obter os percentuais de cada componente como números
      const percentualRoyalties = parseFloat(this.royaltiesFinanceiro) || 0;
      const percentualImpostos = parseFloat(this.imposto) || 0;
      const percentualRepasse = parseFloat(this.repasse) || 0;
      const percentualLucro = parseFloat(this.lucro) || 0;

      // Extrair o valor numérico do texto formatado como percentual
      let percentualDespesasVariaveis = 0;

      if (typeof this.percentualDespesas === "string") {
        // Remove o símbolo % e converte para número
        const valorLimpo = this.percentualDespesas.replace(/[^\d,.]/g, "");
        percentualDespesasVariaveis =
          parseFloat(valorLimpo.replace(",", ".")) || 0;
      } else if (typeof this.percentualDespesas === "number") {
        percentualDespesasVariaveis = this.percentualDespesas;
      }

      // Usar diretamente o valor recebido no campo de despesas representadas como variável
      this.despesasRepresentadasVariavel = this.percentualDespesas;

      // Somar os percentuais para obter o total
      const totalPercentual =
        percentualRoyalties +
        percentualImpostos +
        percentualRepasse +
        percentualLucro +
        percentualDespesasVariaveis;

      // Formatar e atualizar o campo
      const totalFormatado = totalPercentual.toFixed(2).replace(".", ",") + "%";

      this.totalContasVariaveis = totalFormatado;

      // Emitir evento para o componente pai com o valor numérico
      this.$emit("total-contas-variaveis-atualizado", totalPercentual);
    }
  },
  watch: {
    percentualDespesas: {
      handler(newVal) {
        // Atualizar o valor das despesas representadas como variável diretamente
        this.despesasRepresentadasVariavel = newVal;
        // Recalcular o total
        this.calcularTotalContasVariaveis();
      },
      immediate: true
    }
  },
  mounted() {
    // Calcular o total inicial e emitir o evento
    this.$nextTick(() => {
      this.calcularTotalContasVariaveis();
    });
  }
};
</script>

<style scoped>
.variable-accounts-container {
  clear: both;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
}

.variable-accounts-container :deep(.field-group) {
  width: 50%;
  padding-right: 15px;
  box-sizing: border-box;
}

@media screen and (max-width: 768px) {
  .variable-accounts-container :deep(.field-group) {
    width: 100%;
    padding-right: 0;
  }
}
</style>
